import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, Image, Film, Music, Archive, File,
  Download, Trash2, RefreshCw, Loader2, X, ExternalLink,
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL
  ? process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, '') + '/api'
  : '/api';

const PREVIEWABLE = {
  image: (mime) => mime?.startsWith('image/'),
  video: (mime) => mime?.startsWith('video/'),
  audio: (mime) => mime?.startsWith('audio/'),
  pdf:   (mime) => mime === 'application/pdf',
};

function getPreviewType(mime) {
  if (PREVIEWABLE.image(mime)) return 'image';
  if (PREVIEWABLE.video(mime)) return 'video';
  if (PREVIEWABLE.audio(mime)) return 'audio';
  if (PREVIEWABLE.pdf(mime))   return 'pdf';
  return null;
}

function getFileIcon(mimeType) {
  if (!mimeType) mimeType = '';
  if (mimeType.startsWith('image/')) return <Image className="w-8 h-8 text-blue-400" />;
  if (mimeType.startsWith('video/')) return <Film className="w-8 h-8 text-purple-400" />;
  if (mimeType.startsWith('audio/')) return <Music className="w-8 h-8 text-green-400" />;
  if (mimeType === 'application/pdf') return <FileText className="w-8 h-8 text-red-400" />;
  if (mimeType.includes('zip') || mimeType.includes('rar')) return <Archive className="w-8 h-8 text-yellow-500" />;
  if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="w-8 h-8 text-blue-600" />;
  if (mimeType.includes('presentation') || mimeType.includes('powerpoint')) return <FileText className="w-8 h-8 text-orange-500" />;
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <FileText className="w-8 h-8 text-green-600" />;
  return <File className="w-8 h-8 text-slate-400" />;
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function entityLabel(type) {
  if (type === 'interaction') return 'Interaction';
  if (type === 'note') return 'Note';
  if (type === 'task') return 'Task / Follow-up';
  return 'General';
}

const ENTITY_COLORS = {
  interaction: 'bg-blue-50 text-blue-700',
  note: 'bg-amber-50 text-amber-700',
  task: 'bg-green-50 text-green-700',
  general: 'bg-slate-100 text-slate-600',
};

function PreviewModal({ file, onClose }) {
  const type = getPreviewType(file.mime_type);
  const url = file.download_url;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {getFileIcon(file.mime_type)}
            <span className="text-sm font-medium text-slate-800 truncate">{file.file_name}</span>
            <span className="text-xs text-slate-400 shrink-0">{formatBytes(file.file_size)}</span>
          </div>
          <div className="flex items-center gap-1 shrink-0 ml-3">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
              title="Open in new tab"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
            <button
              onClick={onClose}
              className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Preview body */}
        <div className="flex-1 overflow-auto flex items-center justify-center bg-slate-50 rounded-b-xl min-h-0">
          {type === 'image' && (
            <img
              src={url}
              alt={file.file_name}
              className="max-w-full max-h-[75vh] object-contain p-4"
            />
          )}
          {type === 'video' && (
            <video
              src={url}
              controls
              autoPlay={false}
              className="max-w-full max-h-[75vh] rounded"
            />
          )}
          {type === 'audio' && (
            <div className="p-8 flex flex-col items-center gap-4">
              <Music className="w-16 h-16 text-slate-300" />
              <p className="text-sm text-slate-600 font-medium">{file.file_name}</p>
              <audio src={url} controls className="w-80" />
            </div>
          )}
          {type === 'pdf' && (
            <iframe
              src={url}
              title={file.file_name}
              className="w-full rounded-b-xl"
              style={{ height: '75vh' }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const FileGallery = React.forwardRef(function FileGallery(
  { leadId, entityType, entityId, refreshKey },
  ref
) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [preview, setPreview] = useState(null); // file object to preview

  const fetchFiles = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const params = {};
      if (entityType) params.entity_type = entityType;
      if (entityId) params.entity_id = entityId;
      const res = await axios.get(`${API_URL}/leads/${leadId}/files`, { params });
      setFiles(res.data);
    } catch (e) {
      console.error('Failed to load files', e);
    } finally {
      setLoading(false);
    }
  }, [leadId, entityType, entityId]);

  useEffect(() => { fetchFiles(); }, [fetchFiles, refreshKey]);

  React.useImperativeHandle(ref, () => ({ refresh: fetchFiles }));

  async function handleDelete(fileId) {
    setDeletingId(fileId);
    try {
      await axios.delete(`${API_URL}/leads/${leadId}/files/${fileId}`);
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      if (preview?.id === fileId) setPreview(null);
    } catch (e) {
      console.error('Delete failed', e);
    } finally {
      setDeletingId(null);
    }
  }

  async function resolveUrl(file) {
    if (file.download_url) return file.download_url;
    const res = await axios.get(`${API_URL}/leads/${leadId}/files/${file.id}/url`);
    return res.data.url;
  }

  async function handleOpen(file) {
    const previewType = getPreviewType(file.mime_type);
    let url;
    try {
      url = await resolveUrl(file);
    } catch (err) {
      console.error('Failed to resolve file URL', err);
      toast.error('Could not open file. Please try again.');
      return;
    }
    const fileWithUrl = { ...file, download_url: url };

    if (previewType) {
      setPreview(fileWithUrl);
    } else {
      // Non-previewable: open in new tab (will download)
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading files…
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-slate-400">
        <File className="w-12 h-12 mb-3 text-slate-200" />
        <p className="text-sm">No files attached yet</p>
      </div>
    );
  }

  return (
    <>
      {preview && <PreviewModal file={preview} onClose={() => setPreview(null)} />}

      <div className="space-y-2">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-slate-500">{files.length} file{files.length !== 1 ? 's' : ''}</span>
          <button
            type="button"
            onClick={fetchFiles}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        <div className="grid gap-2">
          {files.map((file) => {
            const canPreview = !!getPreviewType(file.mime_type);
            return (
              <div
                key={file.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-slate-100 bg-white hover:bg-slate-50 transition-colors group"
              >
                {/* Thumbnail / icon — click to preview */}
                <button
                  type="button"
                  onClick={() => handleOpen(file)}
                  className="shrink-0 w-10 h-10 flex items-center justify-center rounded overflow-hidden bg-slate-100 hover:ring-2 hover:ring-blue-400 transition-all"
                  title={canPreview ? 'Preview' : 'Open'}
                >
                  {file.mime_type?.startsWith('image/') && file.download_url ? (
                    <img
                      src={file.download_url}
                      alt={file.file_name}
                      className="w-10 h-10 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    getFileIcon(file.mime_type)
                  )}
                </button>

                {/* File info — click to preview */}
                <button
                  type="button"
                  onClick={() => handleOpen(file)}
                  className="flex-1 min-w-0 text-left"
                >
                  <p className="text-sm font-medium text-slate-800 truncate hover:text-blue-600 transition-colors" title={file.file_name}>
                    {file.file_name}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-slate-400">{formatBytes(file.file_size)}</span>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${ENTITY_COLORS[file.entity_type] || ENTITY_COLORS.general}`}
                    >
                      {entityLabel(file.entity_type)}
                    </span>
                    {canPreview && (
                      <span className="text-xs text-blue-400">click to preview</span>
                    )}
                  </div>
                </button>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <a
                    href={file.download_url || '#'}
                    download={file.file_name}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-blue-600 transition-colors"
                    title="Download"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const url = await resolveUrl(file);
                        window.open(url, '_blank', 'noopener,noreferrer');
                      } catch (err) {
                        console.error('Download failed', err);
                        toast.error('Could not retrieve download link. Please try again.');
                      }
                    }}
                  >
                    <Download className="w-4 h-4" />
                  </a>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="w-8 h-8 text-slate-500 hover:text-red-600"
                    onClick={() => handleDelete(file.id)}
                    disabled={deletingId === file.id}
                    title="Delete"
                  >
                    {deletingId === file.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
});

export default FileGallery;
